import React, { useEffect, useRef } from 'react';
import { IElementID } from '@shared/models/project.model';
import { useCommentsStore } from '@client/store/comment/comment.store';
import { Box, List, ListItem, SxProps, Typography } from '@mui/material';
import Comment from '@client/pages/components/Comments/Comment';
import NewComment from '@client/pages/components/Comments/NewComment';
import { useSortedElementCommments } from '@client/store/comment/comments.hook';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { SidePanel } from '@ui/layout';

const ElementComments: React.FC<{ elementId: IElementID }> = ({
  elementId,
}) => {
  const { error, scrollCommentsIntoView, setScrollCommentsIntoView } =
    useCommentsStore(
      ({ error, scrollCommentsIntoView, setScrollCommentsIntoView }) => ({
        error,
        scrollCommentsIntoView,
        setScrollCommentsIntoView,
      }),
    );

  const readonly = useIsReadonly();
  const sorted = useSortedElementCommments(elementId);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollCommentsIntoView) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
        setScrollCommentsIntoView(false);
      });
    }
  }, [scrollCommentsIntoView, setScrollCommentsIntoView]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {!readonly && <NewComment elementId={elementId} />}

      {error?.message.includes('get') ? (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption">{error.message}</Typography>
        </Box>
      ) : (
        sorted.length > 0 && (
          <>
            <SidePanel.Content.Divider />
            <List>
              {sorted.map((comment) => {
                return (
                  <ListItem key={comment.id} disablePadding sx={listItemStyles}>
                    <Comment comment={comment} />
                  </ListItem>
                );
              })}
            </List>
          </>
        )
      )}
    </Box>
  );
};

const listItemStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  paddingTop: 2,
  paddingBottom: 2,
} as const;

export default ElementComments;
